"use client"

import { t } from "@/languages/i18n"
/**
 * RowActionsMenu — 1 primary button + overflow `⋯` dropdown.
 *
 * Linear / GitHub pattern. Replaces ad-hoc 3-4-button-inline action
 * columns across the admin. Destructive items auto-wrap their onClick
 * in Modal.confirm.
 *
 * See LIST_PAGES_SPEC.md for the full contract.
 */

import { MoreOutlined } from "@ant-design/icons"
import { App, Button, Dropdown, Tooltip } from "antd"
import type { MenuProps } from "antd"
import type { HookAPI as ModalHookAPI } from "antd/es/modal/useModal"
import type { ReactNode } from "react"

export interface RowActionItem {
  key: string
  label: ReactNode
  icon?: ReactNode
  danger?: boolean
  disabled?: boolean
  /** Submenu entries — renders as antd SubMenu. */
  children?: RowActionItem[]
  /** If provided, Modal.confirm fires before onClick. */
  confirm?: {
    title: ReactNode
    content?: ReactNode
    okText?: string
    cancelText?: string
  }
  onClick?: () => void
  type?: "divider"
}

interface PrimaryAction {
  label: ReactNode
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  loading?: boolean
}

interface RowActionsMenuProps {
  primary?: PrimaryAction
  items: RowActionItem[]
  /** Tooltip on overflow icon. Defaults to "Thêm hành động". */
  overflowTooltip?: string
  /** Aria label on overflow button for screen readers. */
  ariaLabel?: string
}

const toAntdMenuItems = (
  items: RowActionItem[],
  confirm: ModalHookAPI["confirm"],
): MenuProps["items"] =>
  items
    .filter(item => !item.disabled || item.type === "divider")
    .map(item => {
      if (item.type === "divider") {
        return { type: "divider" as const }
      }

      const handleClick = () => {
        if (!item.onClick) return
        if (item.confirm) {
          confirm({
            title: item.confirm.title,
            content: item.confirm.content,
            okText: item.confirm.okText ?? "Xác nhận",
            cancelText: item.confirm.cancelText ?? "Huỷ",
            okButtonProps: item.danger ? { danger: true } : undefined,
            onOk: () => item.onClick?.(),
          })
        } else {
          item.onClick()
        }
      }

      if (item.children && item.children.length > 0) {
        return {
          key: item.key,
          label: item.label,
          icon: item.icon,
          danger: item.danger,
          children: toAntdMenuItems(item.children, confirm),
        }
      }

      return {
        key: item.key,
        label: item.label,
        icon: item.icon,
        danger: item.danger,
        onClick: handleClick,
      }
    })

export function RowActionsMenu({
  primary,
  items,
  overflowTooltip = t("Thêm hành động"),
  ariaLabel = t("Hành động"),
}: RowActionsMenuProps) {
  const { modal } = App.useApp()
  const menuItems = toAntdMenuItems(items, modal.confirm)
  const hasOverflow = menuItems && menuItems.length > 0

  return (
    <div
      className="flex items-center gap-1 justify-end"
      onClick={e => e.stopPropagation()}
      role="group"
      aria-label={ariaLabel}
    >
      {primary && !primary.disabled && (
        <Button
          size="small"
          icon={primary.icon}
          onClick={primary.onClick}
          loading={primary.loading}
        >
          {primary.label}
        </Button>
      )}
      {hasOverflow && (
        <Dropdown
          menu={{ items: menuItems }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Tooltip title={overflowTooltip}>
            <Button
              size="small"
              type="text"
              icon={<MoreOutlined style={{ fontSize: 18 }} />}
              aria-label={overflowTooltip}
            />
          </Tooltip>
        </Dropdown>
      )}
    </div>
  )
}

export default RowActionsMenu
